// src/pages/AddReservationNotePage.tsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';

const AddReservationNotePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentColor = '#00AEEF';
  const accentHover = '#0095cf';
  const paperBg = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.88) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.92) 100%)';
  const paperBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.04)';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [note, setNote] = useState('');

  const hotelId = searchParams.get('hotelId');
  const reservationId = searchParams.get('reservationId');
  const profileId = searchParams.get('profileId');
  const surname = searchParams.get('surname');
  const redirectToChat = searchParams.get('redirectToChat') === 'true';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!hotelId || !reservationId) {
      setError(t('onboarding.missingParams') || 'Missing required parameters');
      return;
    }

    if (!note.trim()) {
      setError(t('onboarding.specialRequestEmpty') || 'Please enter your request');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/guest-special-request-mail', {
        note: note.trim(),
      });
      setSuccess(true);

      setTimeout(() => {
        const params = new URLSearchParams({
          hotelId: hotelId!,
          reservationId: reservationId!,
        });
        if (profileId) params.append('profileId', profileId);
        if (surname) params.append('surname', surname);
        if (redirectToChat) params.append('redirectToChat', 'true');
        navigate(`/guest-onboarding?${params.toString()}`);
      }, 1500);
    } catch (error: any) {
      console.error('Failed to save note:', error);
      setError(error.response?.data?.message || t('onboarding.failedToSaveNote') || 'Failed to save note. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    const params = new URLSearchParams({
      hotelId: hotelId!,
      reservationId: reservationId!,
    });
    if (profileId) params.append('profileId', profileId);
    if (surname) params.append('surname', surname);
    if (redirectToChat) params.append('redirectToChat', 'true');
    navigate(`/guest-onboarding?${params.toString()}`);
  };

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.9)',
      color: isDark ? '#f8fafc' : '#1f2937',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        borderWidth: 1.5,
        transition: 'all 0.3s ease',
      },
      '&:hover': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.78)' : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark
          ? '0 8px 22px rgba(0, 0, 0, 0.45)'
          : '0 4px 12px rgba(0, 174, 239, 0.12)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2,
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark
          ? '0 12px 30px rgba(0, 174, 239, 0.3)'
          : '0 8px 20px rgba(0, 174, 239, 0.18)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2.5,
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: isDark ? 'rgba(248, 250, 252, 0.7)' : '#6b7280',
      '&.Mui-focused': {
        color: accentColor,
        fontWeight: 700,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 500,
      color: isDark ? '#f8fafc' : '#1f2937',
    },
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 7,
          background: paperBg,
          backdropFilter: 'blur(18px)',
          border: paperBorder,
          boxShadow: isDark
            ? '0 25px 70px rgba(0, 0, 0, 0.6)'
            : '0 30px 80px rgba(15, 23, 42, 0.12)',
        }}
      >
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={isLoading || success}
              sx={{
                mb: 2,
                textTransform: 'none',
                color: accentColor,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                },
              }}
            >
              {t('login.back')}
            </Button>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 700, color: accentColor }}
              >
                {t('onboarding.specialRequestPageTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('onboarding.specialRequestPageSubtitle')}
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2.5,
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)',
                animation: 'slideDown 0.3s ease-out',
                '@keyframes slideDown': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(-10px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2.5,
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
              }}
            >
              {t('onboarding.noteSaved') || 'Your note has been successfully saved!'}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={8}
              label={t('onboarding.specialRequestFieldLabel')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('onboarding.specialRequestPlaceholder')}
              disabled={isLoading || success}
              sx={{
                ...textFieldSx,
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  ...textFieldSx['& .MuiOutlinedInput-root'],
                  '& textarea': {
                    resize: 'vertical',
                  },
                },
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isLoading || success}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                  borderWidth: 1.5,
                  color: '#6b7280',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.6)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: accentColor,
                    borderWidth: 2,
                    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.85)' : 'rgba(0, 0, 0, 0.04)',
                    transform: 'translateY(-1px)',
                    boxShadow: isDark
                      ? '0 6px 18px rgba(0, 0, 0, 0.45)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                {t('login.back')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading || success}
                sx={{
                  px: 4,
                  py: 1.5,
                  backgroundColor: accentColor,
                  boxShadow: '0 18px 35px rgba(0, 174, 239, 0.35)',
                  '&:hover': {
                    backgroundColor: accentHover,
                    boxShadow: '0 18px 40px rgba(0, 174, 239, 0.45)',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                    boxShadow: 'none',
                    color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#94a3b8',
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : (t('onboarding.save') || 'Save')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
  );
};

export default AddReservationNotePage;
